import { Service, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import _moment from 'moment';
import { Observable } from 'rxjs';

import { DataCamp, dataIso } from '../forms/campuri';

import { AppConfig } from '../config/app.config';
import { Medic, MedicEmcTotal } from '../models/medic.model';
import { DocAvizare, DocCertificat } from '../models/medic-doc.model';
import { Transfer } from '../models/medic-actiuni.model';
import { Judet, Registru } from '../models/registre.model';
import { ApiAuthResponse, User, UserLogin } from '../models/user.model';
import {
    ImiEveniment, ImiEvenimentDetaliu, ImiEvenimentListItem, ImiNotificareDetaliu, ImiNotificareListItem, ImiNotificareTip,
    ImiNotificariFiltre, ImiSolutionareReq, ImiImportRezultat, ImiNotificareCuCandidati,
    ImiCreareEvenimentReq, CautareFuzzyResp, ImiSumar, ImiCorectieEvenimentReq, ImiCorectieNotificareReq, MedicCheckPreliminarResp,
} from '../models/imi.model';
import {
    CerereNemembruCuCautari, CerereNemembruDetaliu, CerereNemembruList, NemembruAnuleazaReq, NemembruConfirmaReq,
    NemembruFiltre, NemembruRespingeReq,
} from '../models/nemembru.model';

// raspunsul standard al API-ului pentru operatiile care intorc doar un mesaj
export interface ApiMesaj {
    msg: string;
}

// parametrii de query acceptati de HttpClient
export type ParametriApi = HttpParams | Record<string, string | number | boolean | readonly (string | number | boolean)[]>;

export interface TransferV2Api {
    medicId: string;
    formValues: object;
    transferId?: string;
    pas?: string;
    isNew?: boolean;
    judetSursa?: boolean;
}

@Service()
export class ApiService {
    private http = inject(HttpClient);

    private apiUrl = AppConfig.endpoints.apiURL;
    private apiAuthUrl = AppConfig.endpoints.apiAuthURL;

    callLogin(user: UserLogin) {
        const formData = new FormData();
        formData.append('username', user.email);
        formData.append('password', user.password);

        return this.http.post<ApiAuthResponse>(`${this.apiAuthUrl}`, formData);
    }

    callRefreshToken(refreshToken: string) {
        let headers = new HttpHeaders();
        headers = headers.append('Authorization', 'Bearer ' + refreshToken);
        return this.http.get<ApiAuthResponse>(`${this.apiAuthUrl}/refresh`, { headers: headers });
    }

    getUserLista(): Observable<User[]> {
        return this.http.get<User[]>(`${this.apiUrl}/admin/users`, {});
    }

    callRegistre(): Observable<Registru> {
        return this.http.get<Registru>(`${this.apiUrl}/registre`);
    }

    getJudeteList(): Observable<Judet[]> {
        return this.http.get<Judet[]>(`${this.apiUrl}/registre/judet`);
    }

    getRegistre(): Observable<Registru> {
        return this.http.get<Registru>(`${this.apiUrl}/registre`);
    }

    cautareMedic<T = unknown>(data: string, endpoint: string): Observable<T> {
        // next/prev din raspuns vin ca "<valoare>&page=N"; pagina se trimite separat,
        // iar valoarea codificata, ca un "&" din cautare sa nu strice query-ul
        const match = data.match(/^(.*?)(?:&page=-?\d+)*&page=(-?\d+)$/);
        let params = new HttpParams().set('search_value', match ? match[1] : data);
        if (match) {
            params = params.set('page', match[2]);
        }
        return this.http.get<T>(`${this.apiUrl}/dashboard/search/${endpoint}`, { params });
    }

    getMedicData(idMedic: string): Observable<Medic> {
        return this.http.get<Medic>(`${this.apiUrl}/medic/${idMedic}`);
    }

    checkCNP<T = unknown>(data: string): Observable<T> {
        const params = new HttpParams().set('value', data);
        return this.http.get<T>(`${this.apiUrl}/medic/check/cnp`, { params });
    }

    checkEmail<T = unknown>(data: string): Observable<T> {
        const params = new HttpParams().set('value', data);
        return this.http.get<T>(`${this.apiUrl}/medic/check/email`, { params });
    }

    // M1: CNP/email deja folosite + alerte IMI potrivite, inaintea formularului complet
    checkPreliminar(cnp: string, nume: string, prenume: string, email: string): Observable<MedicCheckPreliminarResp> {
        const params = new HttpParams().set('cnp', cnp).set('nume', nume).set('prenume', prenume).set('email', email);
        return this.http.get<MedicCheckPreliminarResp>(`${this.apiUrl}/medic/check/preliminar`, { params });
    }

    addMedicNew<T = unknown>(data: unknown): Observable<T> {
        return this.http.post<T>(`${this.apiUrl}/medic`, data);
    }

    addMedicItem<T = unknown>(data: unknown, endpoint: string, medicId: string): Observable<T> {
        return this.http.post<T>(`${this.apiUrl}/medic/${medicId}/${endpoint}`, data);
    }

    addMedicItem2<T = unknown>(data: unknown, endpoint: string, medicId: string): Observable<T> {
        if (data) {
            return this.http.post<T>(`${this.apiUrl}/medic/${medicId}/${endpoint}`, data);
        } else {
            return this.http.post<T>(`${this.apiUrl}/${endpoint}`, null);
        }
    }

    editMedicItem<T = unknown>(data: unknown, endpoint: string, medicId: string, itemId: string | undefined): Observable<T> {
        if (itemId) {
            return this.http.patch<T>(`${this.apiUrl}/medic/${medicId}/${endpoint}/${itemId}`, data);
        } else {
            return this.http.patch<T>(`${this.apiUrl}/medic/${medicId}/${endpoint}`, data);
        }
    }

    // TODO: de modificat get, gen, del si print pt doc generic

    getCertDraft(medicId: string): Observable<DocCertificat> {
        return this.http.get<DocCertificat>(`${this.apiUrl}/medic/${medicId}/doc/certificat/draft`);
    }

    genCert<T = unknown>(medicId: string): Observable<T> {
        return this.http.post<T>(`${this.apiUrl}/medic/${medicId}/doc/certificat`, null);
    }

    delCert<T = unknown>(medicId: string, certId: string, motiv: unknown): Observable<T> {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
            body: motiv,
        };
        return this.http.delete<T>(`${this.apiUrl}/medic/${medicId}/doc/certificat/${certId}`, options);
    }

    intrerupeCert<T = unknown>(medicId: string, certId: string, motiv: unknown): Observable<T> {
        return this.http.patch<T>(`${this.apiUrl}/medic/${medicId}/doc/certificat/${certId}`, motiv);
    }

    printCert(medicId: string, certId: string): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/medic/${medicId}/doc/certificat/${certId}/pdf`, { responseType: 'blob' });
    }

    getAvizareDraft(medicId: string): Observable<DocAvizare> {
        return this.http.get<DocAvizare>(`${this.apiUrl}/medic/${medicId}/doc/avizare/draft`);
    }

    genAvizare<T = unknown>(medicId: string, avizare: unknown): Observable<T> {
        return this.http.post<T>(`${this.apiUrl}/medic/${medicId}/doc/avizare`, avizare);
    }

    delAvizare<T = unknown>(medicId: string, avizId: string, motiv: unknown): Observable<T> {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
            body: motiv,
        };
        return this.http.delete<T>(`${this.apiUrl}/medic/${medicId}/doc/avizare/${avizId}`, options);
    }

    intrerupeAvizare<T = unknown>(medicId: string, avizId: string, motiv: unknown): Observable<T> {
        return this.http.patch<T>(`${this.apiUrl}/medic/${medicId}/doc/avizare/${avizId}`, motiv);
    }

    printAvizare(medicId: string, avizId: string): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/medic/${medicId}/doc/avizare/${avizId}/pdf`, { responseType: 'blob' });
    }

    startTransfer<T = unknown>(data: { medicId: string; data: unknown }): Observable<T> {
        return this.http.post<T>(`${this.apiUrl}/medic/${data.medicId}/actiuni/transfer`, data.data);
    }

    startTransferV2(data: TransferV2Api): Observable<Transfer> {
        return this.http.post<Transfer>(`${this.apiUrl}/medic/${data.medicId}/actiuni/transfer`, data.formValues);
    }

    stopTransfer<T = unknown>(medicId: string, transferId: string | null | undefined, data: unknown): Observable<T> {
        return this.http.put<T>(`${this.apiUrl}/medic/${medicId}/actiuni/transfer/${transferId}`, data);
    }

    transferV2(data: TransferV2Api): Observable<Transfer> {
        if (data.pas === 'delete') {
            const options = {
                body: {
                    comentariu: (data.formValues as { comentariu?: string }).comentariu,
                },
            };
            return this.http.delete<Transfer>(
                `${this.apiUrl}/medic/${data.medicId}/actiuni/transfer/${data.transferId}`,
                options,
            );
        } else {
            if (data.isNew) {
                return this.http.post<Transfer>(
                    `${this.apiUrl}/medic/${data.medicId}/actiuni/transfer/${data.transferId}/${data.pas}`,
                    data.formValues,
                );
            } else {
                return this.http.patch<Transfer>(
                    `${this.apiUrl}/medic/${data.medicId}/actiuni/transfer/${data.transferId}/${data.pas}`,
                    data.formValues,
                );
            }
        }
    }

    getTransfer<T = unknown>(endpoint: string): Observable<T> {
        return this.http.get<T>(`${this.apiUrl}/cmj/${endpoint}`);
    }

    getCpc<T = unknown>(endpoint: string, full: string | number | boolean): Observable<T> {
        return this.http.get<T>(`${this.apiUrl}/cmj/${endpoint}/${full}`);
    }

    changeStatus<T = unknown>(medicId: string, data: unknown): Observable<T> {
        return this.http.post<T>(`${this.apiUrl}/medic/${medicId}/actiuni/status`, data);
    }

    getItem<T = unknown>(endpoint: string): Observable<T> {
        return this.http.get<T>(`${this.apiUrl}/${endpoint}`);
    }

    postItem<T = unknown>(data: unknown, endpoint: string): Observable<T> {
        if (data) {
            return this.http.post<T>(`${this.apiUrl}/${endpoint}`, data);
        } else {
            return this.http.post<T>(`${this.apiUrl}/${endpoint}`, null);
        }
    }

    patchItem<T = unknown>(data: unknown, endpoint: string): Observable<T> {
        if (data) {
            return this.http.patch<T>(`${this.apiUrl}/${endpoint}`, data);
        } else {
            return this.http.patch<T>(`${this.apiUrl}/${endpoint}`, null);
        }
    }

    putItem<T = unknown>(data: unknown, endpoint: string): Observable<T> {
        return this.http.put<T>(`${this.apiUrl}/${endpoint}`, data);
    }

    delItem<T = unknown>(params: ParametriApi | null | undefined, endpoint: string): Observable<T> {
        if (params) {
            return this.http.delete<T>(`${this.apiUrl}/${endpoint}`, {
                // params: { mid: data.mid }
                params: params,
            });
        } else {
            return this.http.delete<T>(`${this.apiUrl}/${endpoint}`);
        }
    }

    delItem2<T = unknown>(data: unknown, endpoint: string): Observable<T> {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
            body: data,
        };
        return this.http.delete<T>(`${this.apiUrl}/${endpoint}`, options);
    }

    printItem(endpoint: string): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/${endpoint}`, { responseType: 'blob' });
    }

    downloadRaportXslx(tip: string): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/cmj/raport/xlsx/medic/${tip}`, { responseType: 'blob' });
    }

    apiGetReport(url: string, isAdmin: boolean, params: ParametriApi | undefined): Observable<Blob> {
        if (isAdmin) {
            return this.http.get(`${this.apiUrl}/admin/rapoarte/${url}`, { params: params, responseType: 'blob' });
        } else {
            return this.http.get(`${this.apiUrl}/cmj/raport/${url}`, { params: params, responseType: 'blob' });
        }
    }

    getFile(url: string, params: ParametriApi | null | undefined): Observable<Blob> {
        if (params) {
            return this.http.get(`${this.apiUrl}/${url}`, { params: params, responseType: 'blob' });
        } else {
            return this.http.get(`${this.apiUrl}/${url}`, { responseType: 'blob' });
        }
    }

    postRaportXslx(tip: string, data: unknown): Observable<Blob> {
        return this.http.post(`${this.apiUrl}/cmj/raport/xlsx/${tip}`, data, { responseType: 'blob' });
    }

    getMedicEmcTotal(medicId: string, valoare: DataCamp | null): Observable<MedicEmcTotal> {
        // fara data aleasa se cere totalul la ziua de azi, dupa ceasul local (nu UTC)
        const qDate = (dataIso(valoare) ?? _moment().format('YYYY-MM-DD')).split('T')[0];
        return this.http.get<MedicEmcTotal>(`${this.apiUrl}/medic/${medicId}/emc/total/${qDate}`);
    }

    getMedicEmcExcel(medicId: string): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/medic/${medicId}/emc/export`, { responseType: 'blob' });
    }

    resetRegMedDBFull<T = unknown>(): Observable<T> {
        return this.http.get<T>(`${this.apiUrl}/admin/medic/regmed-all`);
    }

    // IMI - numaratoare pentru pagina de start
    getImiSumar(): Observable<ImiSumar> {
        return this.http.get<ImiSumar>(`${this.apiUrl}/imi/sumar`);
    }

    // IMI - coada notificari brute (Ecran A1)
    getImiNotificari(tip: ImiNotificareTip, filtre: ImiNotificariFiltre): Observable<ImiNotificareListItem[]> {
        let params = new HttpParams();
        if (filtre.activ !== undefined && filtre.activ !== null) {
            params = params.set('activ', filtre.activ);
        }
        if (filtre.are_eveniment !== undefined && filtre.are_eveniment !== null) {
            params = params.set('are_eveniment', filtre.are_eveniment);
        }
        if (filtre.acknowledged !== undefined && filtre.acknowledged !== null) {
            params = params.set('acknowledged', filtre.acknowledged);
        }
        if (filtre.search) {
            params = params.set('search', filtre.search);
        }
        if (filtre.anulat) {
            params = params.set('anulat', true);
        }
        return this.http.get<ImiNotificareListItem[]>(`${this.apiUrl}/imi/notificari/${tip}`, { params });
    }

    // IMI - detaliu notificare + candidati de match (Ecran A2)
    getImiNotificare(tip: ImiNotificareTip, id: string): Observable<ImiNotificareCuCandidati> {
        return this.http.get<ImiNotificareCuCandidati>(`${this.apiUrl}/imi/notificari/${tip}/${id}`);
    }

    // IMI - confirmarea (acknowledge) notificarii de catre Avizari (item D, Ecran A2)
    postImiAcknowledge(tip: ImiNotificareTip, id: string): Observable<ImiNotificareDetaliu> {
        return this.http.post<ImiNotificareDetaliu>(`${this.apiUrl}/imi/notificari/${tip}/${id}/acknowledge`, {});
    }

    // IMI - creare dosar din candidatul validat (Ecran A2)
    postImiEveniment(data: ImiCreareEvenimentReq): Observable<ImiEveniment> {
        return this.http.post<ImiEveniment>(`${this.apiUrl}/imi/evenimente`, data);
    }

    // cautare fuzzy medici, folosita la cautarea manuala din A2
    searchMedicFuzzy(value: string, page = 1): Observable<CautareFuzzyResp> {
        const params = new HttpParams().set('search_value', value).set('page', page);
        return this.http.get<CautareFuzzyResp>(`${this.apiUrl}/dashboard/search-fuzzy/cmr`, { params });
    }

    // cautare exacta (substring pe nume+prenume+nume anterior, CNP, CUIM), fara scor -
    // folosita la cautarea manuala din A2 pentru potrivire integrala
    searchMedicExact(value: string, page = 1): Observable<CautareFuzzyResp> {
        const params = new HttpParams().set('search_value', value).set('page', page);
        return this.http.get<CautareFuzzyResp>(`${this.apiUrl}/dashboard/search/cmr`, { params });
    }

    // IMI - import manual din IMI (doar admin), apoi lista se reincarca
    postImiImportManual(tip: ImiNotificareTip): Observable<ImiImportRezultat> {
        return this.http.post<ImiImportRezultat>(`${this.apiUrl}/imi/import/manual/${tip}`, null);
    }

    // IMI - dosare (imi_evenimente)
    getImiEvenimente(filtre: { status?: string; judet_id?: number | null; overdue?: boolean }): Observable<ImiEvenimentListItem[]> {
        let params = new HttpParams();
        if (filtre.status) {
            params = params.set('status', filtre.status);
        }
        if (filtre.judet_id) {
            params = params.set('judet_id', filtre.judet_id);
        }
        if (filtre.overdue) {
            params = params.set('overdue', true);
        }
        return this.http.get<ImiEvenimentListItem[]>(`${this.apiUrl}/imi/evenimente`, { params });
    }

    getImiEveniment(id: string): Observable<ImiEvenimentDetaliu> {
        return this.http.get<ImiEvenimentDetaliu>(`${this.apiUrl}/imi/evenimente/${id}`);
    }

    patchImiEveniment(id: string, data: ImiSolutionareReq): Observable<ImiEveniment> {
        return this.http.patch<ImiEveniment>(`${this.apiUrl}/imi/evenimente/${id}`, data);
    }

    // IMI - corectie dosar, doar admin (Ecran D1)
    patchImiEvenimentAdmin(id: string, data: ImiCorectieEvenimentReq): Observable<ImiEveniment> {
        return this.http.patch<ImiEveniment>(`${this.apiUrl}/admin/imi/evenimente/${id}`, data);
    }

    // IMI - corectie notificare bruta, doar admin (Ecran D2)
    patchImiNotificareAdmin(tip: ImiNotificareTip, id: string, data: ImiCorectieNotificareReq): Observable<ImiNotificareDetaliu> {
        return this.http.patch<ImiNotificareDetaliu>(`${this.apiUrl}/admin/imi/notificari/${tip}/${id}`, data);
    }

    // adeverinta de nemembru - cererile venite din DMS (doar admin si Avizari)
    getNemembruCereri(filtre: NemembruFiltre): Observable<CerereNemembruList> {
        let params = new HttpParams();
        Object.entries(filtre).forEach(([cheie, valoare]) => {
            if (valoare !== null && valoare !== undefined) {
                params = params.set(cheie, valoare);
            }
        });
        return this.http.get<CerereNemembruList>(`${this.apiUrl}/nemembru`, { params });
    }

    getNemembruCerere(id: string): Observable<CerereNemembruCuCautari> {
        return this.http.get<CerereNemembruCuCautari>(`${this.apiUrl}/nemembru/${id}`);
    }

    postNemembruConfirma(id: string, data: NemembruConfirmaReq): Observable<CerereNemembruDetaliu> {
        return this.http.post<CerereNemembruDetaliu>(`${this.apiUrl}/nemembru/${id}/confirma`, data);
    }

    postNemembruRespinge(id: string, data: NemembruRespingeReq): Observable<CerereNemembruDetaliu> {
        return this.http.post<CerereNemembruDetaliu>(`${this.apiUrl}/nemembru/${id}/respinge`, data);
    }

    postNemembruAnuleaza(id: string, data: NemembruAnuleazaReq): Observable<CerereNemembruDetaliu> {
        return this.http.post<CerereNemembruDetaliu>(`${this.apiUrl}/nemembru/${id}/anuleaza`, data);
    }

    // incarca adeverinta in DMS: 6 confirmata -> 5 incarcata; 502 daca DMS nu raspunde
    postNemembruIncarcaDms(id: string): Observable<CerereNemembruDetaliu> {
        return this.http.post<CerereNemembruDetaliu>(`${this.apiUrl}/nemembru/${id}/incarca-dms`, {});
    }

    // adeverinta cererii confirmate: o pagina per limba, sau doar `limba`
    getNemembruPdf(id: string, limba?: string): Observable<Blob> {
        const params = limba ? new HttpParams().set('limba', limba) : undefined;
        return this.http.get(`${this.apiUrl}/nemembru/${id}/pdf`, { params, responseType: 'blob' });
    }
}
